import {capitalize} from "@mui/material";
import {useSnackbar} from "notistack";
import {useMutation, useQueryClient} from "react-query";
import {useTranslation} from "react-i18next";
import {ApiError, Prompt} from "../../api";
import {
    useCreateAufgabePrompt,
    useDeleteAufgabePrompt,
    useSetActiveAufgabePrompt,
    useUpdatePrompt,
} from "../../client/hooks";
import type {PromptDataShape} from "../../components/aufgaben/AufgabePromptVersionEditor";

export function useErrorMessage() {
    const {t: tErrors} = useTranslation('errors');
    const {t: tAufgaben} = useTranslation('aufgaben');
    return (error: unknown, fallback: string): string => {
        if (error instanceof ApiError) {
            const body = (error.body ?? {}) as { code?: string; message?: string };
            if (body.code) {
                const [ns, key] = body.code.split(':');
                if (ns === 'aufgaben' && key) return tAufgaben(key);
                if (ns === 'errors' && key) return tErrors(key);
                return body.code;
            }
            if (body.message) return body.message;
        }
        if (error instanceof Error) return error.message;
        return fallback;
    };
}


export function usePromptHandlingBase(
    entityId: string,
    invalidateKeys: string[][],
    createFn: (entityId: string, params: {activate: boolean}, data: PromptDataShape) => Promise<Prompt>,
    updateFn: (promptId: string, data: PromptDataShape) => Promise<Prompt>,
    setActiveFn: (entityId: string, promptId: string) => Promise<unknown>,
    deleteFn: (entityId: string, promptId: string) => Promise<Prompt>,
) {
    const {t: translation} = useTranslation('aufgaben');
    const queryClient = useQueryClient();
    const {enqueueSnackbar} = useSnackbar();
    const formatError = useErrorMessage();

    const invalidate = async () => {
        await Promise.all(invalidateKeys.map(key => queryClient.invalidateQueries(key)));
    };

    const createPromptMutation = useMutation(
        ({data, activate}: {data: PromptDataShape; activate: boolean}): Promise<Prompt> =>
            createFn(entityId, {activate}, data),
        {
            onSuccess: async () => { await invalidate(); enqueueSnackbar(capitalize(translation('prompt-version-create-successful')), {variant: 'success'}); },
            onError: (err) => { enqueueSnackbar(formatError(err, capitalize(translation('prompt-version-create-failed'))), {variant: 'error'}); },
        }
    );

    const updatePromptMutation = useMutation(
        ({promptId, data}: {promptId: string; data: PromptDataShape}): Promise<Prompt> =>
            updateFn(promptId, data),
        {
            onSuccess: async () => { await invalidate(); enqueueSnackbar(capitalize(translation('prompt-version-update-successful')), {variant: 'success'}); },
            onError: (err) => { enqueueSnackbar(formatError(err, capitalize(translation('prompt-version-update-failed'))), {variant: 'error'}); },
        }
    );

    const setActivePromptMutation = useMutation(
        (promptId: string) => setActiveFn(entityId, promptId),
        {
            onSuccess: async () => { await invalidate(); enqueueSnackbar(capitalize(translation('prompt-version-activate-successful')), {variant: 'success'}); },
            onError: (err) => { enqueueSnackbar(formatError(err, capitalize(translation('prompt-version-activate-failed'))), {variant: 'error'}); },
        }
    );

    const deletePromptMutation = useMutation(
        (promptId: string): Promise<Prompt> => deleteFn(entityId, promptId),
        {
            onSuccess: async () => { await invalidate(); enqueueSnackbar(capitalize(translation('prompt-version-delete-successful')), {variant: 'success'}); },
            onError: (err) => { enqueueSnackbar(formatError(err, capitalize(translation('prompt-version-delete-failed'))), {variant: 'error'}); },
        }
    );

    return {translation, createPromptMutation, updatePromptMutation, setActivePromptMutation, deletePromptMutation};
}

// ── thin wrapper for regular (outbound) prompts ───────────────────────────────

export function useAufgabenPromptHandling(aufgabeId: string) {
    const createPrompt = useCreateAufgabePrompt();
    const updatePrompt = useUpdatePrompt();
    const setActivePrompt = useSetActiveAufgabePrompt();
    const deletePrompt = useDeleteAufgabePrompt();
    return usePromptHandlingBase(
        aufgabeId,
        [
            ["api", "v1", "aufgaben", aufgabeId, "prompts"],
            ["api", "v1", "aufgaben", aufgabeId],
            ["api", "v1", "prompts"],
        ],
        createPrompt, updatePrompt, setActivePrompt, deletePrompt,
    );
}
